import type { MessageKeys } from 'next-intl';
import type { RecaptionPrompt, TextPromptWithInputItem } from '@/app/types';
import { stringifyTextPromptWithReference } from './text-prompt-with-reference';

export type ProModeTitleKeys =
  | 'subject.title'
  | 'environment.title'
  | 'style.title'
  | 'motionLevel.title'
  | 'shotSize.title'
  | 'focalLength.title'
  | 'composition.title'
  | 'cameraAngle.title'
  | 'cameraMovement.title'
  | 'specialFilmingTechnique.title';

export type ProTextTranslationFn = (
  key: MessageKeys<IntlMessages, ProModeTitleKeys | 'style.animation' | 'motionLevel.middle'>,
) => string;

const PRO_MODE_TITLE_KEY_MAP: Record<string, ProModeTitleKeys> = {
  subject: 'subject.title',
  environment: 'environment.title',
  style: 'style.title',
  motionLevel: 'motionLevel.title',
  shotSize: 'shotSize.title',
  focalLength: 'focalLength.title',
  composition: 'composition.title',
  cameraAngle: 'cameraAngle.title',
  cameraMovement: 'cameraMovement.title',
  specialFilmingTechnique: 'specialFilmingTechnique.title',
};

/**
 * 获取 ProMode 的标题
 * @param key - 标题字符串
 * @param t - 国际化函数
 * @returns 返回本地化的标题字符串
 */
export const getProModeTitle = (key: string, t: ProTextTranslationFn): string => {
  const titleKey = PRO_MODE_TITLE_KEY_MAP[key];
  return titleKey ? t(titleKey) : key;
};

/**
 * 将 RecaptionPrompt 对象转换为 TextPromptWithInputItem 对象 或 纯文本
 * （关闭 Pro Mode / 复制）
 *
 * @param prompt - 要转换的 RecaptionPrompt 对象
 * @param options - 可选参数
 * @param options.t - 国际化函数
 * @param options.outputFormat - 输出格式，默认是 with-reference，如果传入 text 则返回纯文本
 * @returns 转换后的 TextPromptWithInputItem 对象或纯文本
 */
export const convertProTextPromptToSimple = (
  prompt: RecaptionPrompt,
  options: {
    t: ProTextTranslationFn;
    outputFormat?: 'text' | 'with-reference';
  },
): string | TextPromptWithInputItem => {
  const { subject, environment, ...rest } = prompt;
  const { t, outputFormat = 'with-reference' } = options;

  const textPrompt: TextPromptWithInputItem = [
    {
      type: 'text',
      text: `${getProModeTitle('subject', t)}: `,
    },
    ...subject,
    {
      type: 'text',
      text: `\n${getProModeTitle('environment', t)}: `,
    },
    ...environment,
    {
      type: 'text',
      text: `\n${Object.entries(rest)
        .map(([key, value]) => (value ? `${getProModeTitle(key, t)}: ${value.join(', ')}` : ''))
        .filter((item) => item)
        .join('\n')}`,
    },
  ];

  if (outputFormat === 'text') {
    return stringifyTextPromptWithReference(textPrompt);
  }

  // 如果没有 mention，合并 text node
  const output: TextPromptWithInputItem = [];
  let currentText = '';

  for (const item of textPrompt) {
    if (item.type === 'text') {
      currentText += item.text;
    } else if (item.type === 'mention') {
      if (currentText) {
        output.push({ type: 'text', text: currentText });
        currentText = '';
      }
      output.push(item);
    }
  }

  if (currentText) {
    output.push({ type: 'text', text: currentText });
  }

  return output;
};
